// @flow
import { Route } from 'react-router-dom';
import { Routes } from 'react-router';
import { useSelector } from 'react-redux';

import PrivateRoute from 'insight/routing/PrivateRoute';
import {
  INSIGHT_ADMINISTRATION_SERVICES,
  INSIGHT_ADMINISTRATION_USERS,
  INSIGHT_ANALYTICS,
  INSIGHT_EDGE,
  INSIGHT_FARMER_APP,
  INSIGHT_FARMER_APP_CUSTOMERS,
  INSIGHT_FARMER_APP_DEVICES,
  INSIGHT_GENERAL,
  INSIGHT_GENERAL_NODES_MAP,
  INSIGHT_GENERAL_NODES_MAP_FARMER,
  INSIGHT_GENERAL_NODES_MAP_MANAGER,
  INSIGHT_GENERAL_OVERVIEW,
  INSIGHT_MANAGER,
  INSIGHT_MANAGER_GATEWAYS,
  INSIGHT_MANAGER_SUBSCRIPTIONS,
  INSIGHT_MANAGER_TENANTS,
  INSIGHT_QUOTE_TOOL,
  INSIGHT_SERVICE_OVERVIEW,
  INSIGHT_SERVICE_STATUS_APP,
  INSIGHT_SERVICE_STATUS_MANAGER
} from 'insight/routing/InsightRouteConstants';
import PublicRoute from 'insight/routing/PublicRoute';
import 'mapbox-gl/dist/mapbox-gl.css';
import General from './insight/pages/general/General';
import GeneralOverview from './insight/pages/general/GeneralOverview';
import GeneralNodesGraph from './insight/pages/general/GeneralNodesGraph';
import GeneralFarmerNodesGraph from './insight/pages/general/GeneralFarmerNodesGraph';
import GeneralManagerNodesGraph from './insight/pages/general/GeneralManagerNodesGraph';
import ServiceOverview from './insight/pages/serviceOverview/ServiceOverview';
import ServiceStatusApp from './insight/pages/serviceOverview/ServiceStatusApp';
import ServiceStatusManager from './insight/pages/serviceOverview/ServiceStatusManager';
import ManagerTenants from './insight/pages/manager/ManagerTenants';
import Manager from './insight/pages/manager/Manager';
import ManagerSubscriptions from './insight/pages/manager/ManagerSubscriptions';
import ManagerGateways from './insight/pages/manager/ManagerGateways';
import FarmerApp from './insight/pages/farmerapp/FarmerApp';
import FarmerAppCustomers from './insight/pages/farmerapp/FarmerAppCustomers';
import FarmerAppDevices from './insight/pages/farmerapp/FarmerAppDevices';
import AdminUsers from './insight/pages/admin/AdminUsers';
import AdminServices from './insight/pages/admin/AdminServices';
import CompleteRegistration from './common/pages/CompleteRegistration';
import ResetPassword from './common/pages/ResetPassword';
import RequestResetPassword from './common/pages/RequestResetPassword';
import PublicWorldMapWrapper from './insight/components/worldmap/PublicWorldMapWrapper';
import RegisterUser from './common/pages/RegisterUser';
import InsightPageNotFound from './insight/pages/InsightPageNotFound';
import Login from './common/pages/Login';
import Analytics from './insight/pages/analytics/Analytics';
import Edge from './insight/pages/edge/Edge';
import {
  COMPLETE_REGISTRATION,
  LOGIN,
  PUBLIC_WORLD_MAP,
  REGISTER_USER,
  REQUEST_RESET_PASSWORD,
  RESET_PASSWORD
} from './common/routing/RouteConstants';
import { getInvitationEnabledSetting, getRegistrationEnabledSetting } from './store/selector/ApplicationSelector';
import QuoteTool from './insight/pages/quote-tool/QuoteTool';

const InsightApp = (): React$Node => {
  const registrationEnabled = useSelector(getRegistrationEnabledSetting);
  const invitationEnabled = useSelector(getInvitationEnabledSetting);

  return (
    <Routes>
      <Route
        path={INSIGHT_GENERAL}
        element={
          <PrivateRoute internalRestricted>
            <General />
          </PrivateRoute>
        }
      >
        <Route path={INSIGHT_GENERAL_OVERVIEW} element={<GeneralOverview />} />
        <Route path={INSIGHT_GENERAL_NODES_MAP} element={<GeneralNodesGraph />}>
          <Route path={INSIGHT_GENERAL_NODES_MAP_MANAGER} element={<GeneralManagerNodesGraph />} />
          <Route path={INSIGHT_GENERAL_NODES_MAP_FARMER} element={<GeneralFarmerNodesGraph />} />
        </Route>
      </Route>
      <Route
        path={INSIGHT_SERVICE_OVERVIEW}
        element={
          <PrivateRoute internalRestricted>
            <ServiceOverview />
          </PrivateRoute>
        }
      >
        <Route path={INSIGHT_SERVICE_STATUS_APP} element={<ServiceStatusApp />} />
        <Route path={INSIGHT_SERVICE_STATUS_MANAGER} element={<ServiceStatusManager />} />
      </Route>
      <Route
        path={INSIGHT_MANAGER}
        element={
          <PrivateRoute internalRestricted>
            <Manager />
          </PrivateRoute>
        }
      >
        <Route path={INSIGHT_MANAGER_TENANTS} element={<ManagerTenants />} />
        <Route path={INSIGHT_MANAGER_SUBSCRIPTIONS} element={<ManagerSubscriptions />} />
        <Route path={INSIGHT_MANAGER_GATEWAYS} element={<ManagerGateways />} />
      </Route>
      <Route
        path={INSIGHT_FARMER_APP}
        element={
          <PrivateRoute internalRestricted>
            <FarmerApp />
          </PrivateRoute>
        }
      >
        <Route path={INSIGHT_FARMER_APP_CUSTOMERS} element={<FarmerAppCustomers />} />
        <Route path={INSIGHT_FARMER_APP_DEVICES} element={<FarmerAppDevices />} />
      </Route>
      <Route
        path={INSIGHT_ANALYTICS}
        element={
          <PrivateRoute internalRestricted>
            <Analytics />
          </PrivateRoute>
        }
      />
      <Route
        path={INSIGHT_EDGE}
        element={
          <PrivateRoute internalRestricted>
            <Edge />
          </PrivateRoute>
        }
      />
      <Route
        path={INSIGHT_QUOTE_TOOL}
        element={
          <PrivateRoute internalRestricted>
            <QuoteTool />
          </PrivateRoute>
        }
      />
      <Route
        path={INSIGHT_ADMINISTRATION_USERS}
        element={
          <PrivateRoute restricted>
            <AdminUsers />
          </PrivateRoute>
        }
      />
      <Route
        path={INSIGHT_ADMINISTRATION_SERVICES}
        element={
          <PrivateRoute restricted>
            <AdminServices />
          </PrivateRoute>
        }
      />
      <Route
        path={LOGIN}
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path={COMPLETE_REGISTRATION}
        element={
          <PublicRoute>
            <CompleteRegistration />
          </PublicRoute>
        }
      />
      <Route
        path={RESET_PASSWORD}
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      <Route
        path={REQUEST_RESET_PASSWORD}
        element={
          <PublicRoute>
            <RequestResetPassword />
          </PublicRoute>
        }
      />
      <Route
        path={PUBLIC_WORLD_MAP}
        element={
          <PublicRoute noRedirect withLogo={false}>
            <PublicWorldMapWrapper />
          </PublicRoute>
        }
      />
      {registrationEnabled || invitationEnabled ? (
        <Route
          path={REGISTER_USER}
          element={
            <PublicRoute>
              <RegisterUser />
            </PublicRoute>
          }
        />
      ) : null}
      <Route path={'*'} element={<InsightPageNotFound />} />
    </Routes>
  );
};

export default InsightApp;
